import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable, finalize } from 'rxjs';
import { AppConfigurationService } from '../../core/services/app-configuration.service';
import { showAlertDialog } from '../../shared/helpers/showAlertDialog';
import { AlertType } from '../../shared/models/alertType';
import { ConsultantNotificationRule, UpdateConsultantNotificationRuleTemplatePayload } from '../../shared/models/app-configuration';
import { SectionCardComponent } from '../roteirizacao/components/section-card/section-card.component';
import { CustomToggleComponent } from '../roteirizacao/components/custom-toggle/custom-toggle.component';
import { EditNotificationTemplateDialogComponent } from '../system-settings/dialogs/edit-notification-template-dialog.component';
import { PendingChangesAware } from '../../core/guards/pending-changes.guard';

interface NotificationRuleViewModel extends ConsultantNotificationRule {
  icon: string;
  description: string;
}

const RULE_METADATA: Record<string, { icon: string; description: string }> = {
  BeforeStartingDay: {
    icon: 'fa-solid fa-sun',
    description: 'Notificação enviada antes do início da rota jornada',
  },
  LateToRouteStart: {
    icon: 'fa-solid fa-sun',
    description: 'Notificação enviada caso o consultor se atrase ao iniciar a rota',
  },
  ApproachingNextStore: {
    icon: 'fa-solid fa-store',
    description: 'Notificação quando o consultor está próximo da próxima visita',
  },
  LeavingStoreWithoutCheckout: {
    icon: 'fa-solid fa-store-slash',
    description: 'Alerta quando o consultor sai da loja sem registrar check-out',
  },
  BeforeLunchTime: {
    icon: 'fa-solid fa-utensils',
    description: 'Lembrete sobre o horário de almoço programado',
  },
  ApproachingLastStore: {
    icon: 'fa-solid fa-hourglass-half',
    description: 'Notificação quando está próximo da última visita programada',
  },
  AfterLastCheckout: {
    icon: 'fa-solid fa-stopwatch',
    description: 'Confirmação após finalizar a última visita do dia',
  },
  BeforeEndingDay: {
    icon: 'fa-solid fa-moon',
    description: 'Lembrete para finalizar as atividades do dia',
  },
};

const RULE_ORDER = [
  'BeforeStartingDay',
  'LateToRouteStart',
  'ApproachingNextStore',
  'LeavingStoreWithoutCheckout',
  'BeforeLunchTime',
  'ApproachingLastStore',
  'AfterLastCheckout',
  'BeforeEndingDay',
];

@Component({
  selector: 'app-app-configuration',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, SectionCardComponent, CustomToggleComponent],
  templateUrl: './app-configuration.component.html',
  styleUrl: './app-configuration.component.scss',
})
export class AppConfigurationComponent implements OnInit, PendingChangesAware {
  readonly form = this.formBuilder.group({
    checkInRadiusMeters: [150, [Validators.required, Validators.min(1)]],
    exitRadiusMeters: [250, [Validators.required, Validators.min(1)]],
  });

  requireCheckInOutOfBaseJustification = false;
  requireOvernightLocationJustification = false;
  requireCheckOutOutOfBaseJustification = false;

  notificationRules: NotificationRuleViewModel[] = [];
  isLoading = false;
  isSaving = false;

  private initialSnapshot = {
    checkInRadiusMeters: 150,
    exitRadiusMeters: 250,
    requireCheckInOutOfBaseJustification: false,
    requireOvernightLocationJustification: false,
    requireCheckOutOutOfBaseJustification: false,
  };

  private initialRules = new Map<string, { isEnabled: boolean; template: string }>();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly appConfigService: AppConfigurationService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.loadConfiguration();
  }

  preventNonIntegerInput(event: KeyboardEvent): void {
    if (['e', 'E', '.', ',', '+', '-'].includes(event.key)) {
      event.preventDefault();
    }
  }

  get exitRadiusError(): boolean {
    const formValue = this.form.getRawValue();
    return (formValue.exitRadiusMeters ?? 0) <= (formValue.checkInRadiusMeters ?? 0);
  }

  hasPendingChanges(): boolean {
    const formValue = this.form.getRawValue();

    if (formValue.checkInRadiusMeters !== this.initialSnapshot.checkInRadiusMeters) return true;
    if (formValue.exitRadiusMeters !== this.initialSnapshot.exitRadiusMeters) return true;
    if (this.requireCheckInOutOfBaseJustification !== this.initialSnapshot.requireCheckInOutOfBaseJustification) return true;
    if (this.requireOvernightLocationJustification !== this.initialSnapshot.requireOvernightLocationJustification) return true;
    if (this.requireCheckOutOutOfBaseJustification !== this.initialSnapshot.requireCheckOutOutOfBaseJustification) return true;

    return this.notificationRules.some((rule) => {
      const initial = this.initialRules.get(rule.type);
      if (!initial) return false;
      return initial.isEnabled !== rule.isEnabled;
    });
  }

  openEditTemplate(rule: NotificationRuleViewModel): void {
    this.dialog
      .open(EditNotificationTemplateDialogComponent, {
        data: {
          type: rule.type,
          name: rule.name,
          template: rule.template,
          saveFn: (payload: UpdateConsultantNotificationRuleTemplatePayload) =>
            this.appConfigService.updateNotificationTemplate(payload),
          successTitle: 'Sucesso!',
          successMessage: 'Notificação atualizada com sucesso!',
          errorTitle: 'Ops!',
          errorMessage: 'Ocorreu um erro ao atualizar a notificação. Tente novamente mais tarde.',
        },
        disableClose: true,
      })
      .afterClosed()
      .subscribe((result?: { updated?: boolean; template?: string }) => {
        const updatedTemplate = result?.template;
        if (!result?.updated || !updatedTemplate) return;

        this.notificationRules = this.notificationRules.map((item) =>
          item.type === rule.type ? { ...item, template: updatedTemplate } : item,
        );
      });
  }

  onRuleToggle(type: string, value: boolean): void {
    this.notificationRules = this.notificationRules.map((rule) =>
      rule.type === type ? { ...rule, isEnabled: value } : rule,
    );
  }

  saveChanges(): void {
    if (this.form.invalid || this.exitRadiusError) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSaving = true;
    const formValue = this.form.getRawValue();

    this.appConfigService
      .updateConfiguration({
        requireCheckInOutOfBaseJustification: this.requireCheckInOutOfBaseJustification,
        requireOvernightLocationJustification: this.requireOvernightLocationJustification,
        requireCheckOutOutOfBaseJustification: this.requireCheckOutOutOfBaseJustification,
        checkInRadiusMeters: formValue.checkInRadiusMeters ?? 150,
        exitRadiusMeters: formValue.exitRadiusMeters ?? 250,
        notificationRules: this.notificationRules.map((rule) => ({
          type: rule.type,
          isEnabled: rule.isEnabled,
        })),
      })
      .pipe(finalize(() => (this.isSaving = false)))
      .subscribe({
        next: () => {
          this.saveSnapshot();
          this.form.markAsPristine();
          showAlertDialog(this.dialog, AlertType.Success, 'Sucesso!', 'Configurações atualizadas com sucesso!');
        },
        error: () => {
          showAlertDialog(
            this.dialog,
            AlertType.Error,
            'Erro',
            'Ocorreu um erro ao salvar as configurações. Tente novamente mais tarde.',
          );
        },
      });
  }

  private loadConfiguration(): void {
    this.isLoading = true;

    this.appConfigService
      .getConfiguration()
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (response) => {
          this.form.patchValue({
            checkInRadiusMeters: response.checkInRadiusMeters,
            exitRadiusMeters: response.exitRadiusMeters,
          });

          this.requireCheckInOutOfBaseJustification = response.requireCheckInOutOfBaseJustification;
          this.requireOvernightLocationJustification = response.requireOvernightLocationJustification;
          this.requireCheckOutOutOfBaseJustification = response.requireCheckOutOutOfBaseJustification;

          this.notificationRules = response.notificationRules
            .map((rule) => {
              const metadata = RULE_METADATA[rule.type];
              return {
                ...rule,
                icon: metadata?.icon ?? 'fa-solid fa-bell',
                description: metadata?.description ?? '',
              };
            })
            .sort((a, b) => {
              const aIndex = RULE_ORDER.indexOf(a.type);
              const bIndex = RULE_ORDER.indexOf(b.type);
              const normalizedA = aIndex === -1 ? Number.MAX_SAFE_INTEGER : aIndex;
              const normalizedB = bIndex === -1 ? Number.MAX_SAFE_INTEGER : bIndex;
              return normalizedA - normalizedB;
            });

          this.saveSnapshot();
          this.form.markAsPristine();
        },
        error: () => {
          showAlertDialog(
            this.dialog,
            AlertType.Error,
            'Erro',
            'Não foi possível carregar as configurações do app.',
          );
        },
      });
  }

  private saveSnapshot(): void {
    const formValue = this.form.getRawValue();
    this.initialSnapshot = {
      checkInRadiusMeters: formValue.checkInRadiusMeters ?? 150,
      exitRadiusMeters: formValue.exitRadiusMeters ?? 250,
      requireCheckInOutOfBaseJustification: this.requireCheckInOutOfBaseJustification,
      requireOvernightLocationJustification: this.requireOvernightLocationJustification,
      requireCheckOutOutOfBaseJustification: this.requireCheckOutOutOfBaseJustification,
    };

    this.initialRules = new Map(
      this.notificationRules.map((rule) => [
        rule.type,
        { isEnabled: rule.isEnabled, template: rule.template },
      ]),
    );
  }
}
